import axios from "axios"
import React, { useState } from "react"

import ListingCard from "../../components/ListingCard"
import { Button } from "../../components/shared/Buttons"
import Typography from "../../components/shared/Typography"
import { ToastProvider, useToast } from "../../contexts/ToastContext"
import ImagePickerV2 from "../WebsiteBuilderV2/components/shared/ImagePickerV2"

const ListingCardPageContent = ({ practice = {}, listingCardSettings: initialSettings = {} }) => {
  // Initialize headline from listing card settings, or fall back to title + headline if empty
  const initialHeadline =
    initialSettings?.headline ||
    (practice?.title && practice?.headline
      ? `${practice.title} - ${practice.headline}`
      : practice?.title || practice?.headline || "")
  const [settings, setSettings] = useState({
    headline: initialHeadline,
    imageUrl: initialSettings?.imageUrl || ""
  })
  const [isSaving, setIsSaving] = useState(false)
  const [isMobile, setIsMobile] = useState(false)
  const { showToast } = useToast()

  const handleChange = (e) => {
    setSettings({ ...settings, [e.target.name]: e.target.value })
  }

  const handleImageSelect = (image) => {
    setSettings({ ...settings, imageUrl: image.imageUrl })
  }

  const handleRemoveImage = () => {
    setSettings({ ...settings, imageUrl: "" })
  }

  const handleSave = async () => {
    setIsSaving(true)
    try {
      const response = await axios.patch("/portal/listing_card", { settings })
      if (response.data.success) {
        showToast({ content: "🎉  Listing card saved! ", type: "success", position: "topCenter" })
      } else {
        showToast({ content: "Failed to save settings.", type: "error", position: "topCenter" })
      }
    } catch (err) {
      showToast({ content: "An error occurred while saving.", type: "error", position: "topCenter" })
    } finally {
      setIsSaving(false)
    }
  }

  const previewData = {
    practice,
    listingCardSettings: settings
  }

  const currentImage = settings.imageUrl || practice?.defaultPhotoUrl

  return (
    <div className="mx-auto max-w-7xl p-4 text-black md:p-8">
      <div className="mb-10 lg:mb-12">
        <Typography variant="h3" as="h1">
          Listing card
        </Typography>
        <Typography variant="subtitle" className="text-gray-dark">
          Customize your listing card in practitioner search results
        </Typography>
      </div>

      {/*
        Desktop-First Layout:
        - Base: flex-row (for resolutions > 1023px, including 1024, 1440, etc.)
        - gap-16: Exactly 4rem distance
        - max-[1023px]:flex-col (Stack on tablets and mobile)
      */}
      <div className="max-[1023px]:flex-col max-[1023px]:gap-y-12 flex w-full flex-row items-start gap-16">
        {/* Form Container */}
        <div className="w-full max-w-[540px] shrink-0 rounded-2xl border border-gray-light bg-white p-8 shadow-sm">
          <div className="mb-12">
            <Typography variant="h5" className="mb-2 font-bold text-black">
              Listing Card Image
            </Typography>
            <Typography variant="small" className="mb-6 block leading-5 text-gray-dark">
              Add a high-quality, professional photo of you working with a client.
            </Typography>

            <div className="flex items-start gap-6">
              {currentImage ? (
                <>
                  <div
                    className="h-[120px] w-[180px] shrink-0 overflow-hidden rounded-2xl border border-gray-light bg-gray-ultralight"
                    style={{ aspectRatio: "3/2" }}>
                    <img src={currentImage} className="size-full object-cover" alt="Listing card preview" />
                  </div>
                  <div className="flex flex-col gap-3">
                    <ImagePickerV2
                      practice={practice}
                      onImageSelect={handleImageSelect}
                      fetching={isSaving}
                      currentImageUrl={settings.imageUrl}
                      showCurateTab={true}
                      trigger={
                        <button
                          className="text-left text-[18px] font-bold text-builder-accent underline transition-colors hover:text-[#0b8a91]"
                          disabled={isSaving}>
                          Edit image
                        </button>
                      }
                    />
                    {settings.imageUrl && (
                      <button
                        onClick={handleRemoveImage}
                        disabled={isSaving}
                        className="text-left text-[18px] font-bold text-[#ef4444] transition-colors hover:text-red-700 disabled:opacity-50">
                        Remove
                      </button>
                    )}
                  </div>
                </>
              ) : (
                <ImagePickerV2
                  practice={practice}
                  onImageSelect={handleImageSelect}
                  fetching={isSaving}
                  showCurateTab={true}
                  trigger={
                    <div className="relative box-border flex w-full max-w-md shrink-0 cursor-pointer flex-col content-stretch items-center justify-center gap-1.5 rounded-lg border-2 border-dashed border-gray-light bg-white p-6 transition-colors hover:border-teal">
                      <div className="relative flex size-10 shrink-0 items-center justify-center rounded-full bg-teal/[0.12]">
                        <svg className="size-5 text-teal" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                          />
                        </svg>
                      </div>
                      <p className="text-center text-base font-bold text-teal">upload an image</p>
                      <p className="text-center text-sm text-gray-dark">Supported formats: PNG, JPG, SVG</p>
                    </div>
                  }
                />
              )}
            </div>
          </div>

          <div className="mb-6">
            <Typography variant="h5" className="mb-2 font-bold text-black">
              Listing card headline
            </Typography>
            <Typography variant="small" className="mb-4 block leading-5 text-gray-dark">
              Describe what service you provide for your clients. Keep it concise - maximum 40 characters.
            </Typography>
            <input
              type="text"
              name="headline"
              value={settings.headline}
              onChange={handleChange}
              placeholder="e.g. Massage Therapy & Wellness"
              className="w-full rounded-xl border border-gray-light p-4 text-black placeholder:text-gray-dark/40 focus:border-teal focus:outline-none focus:ring-1 focus:ring-teal"
              maxLength={40}
            />
            <div className="mt-2 flex items-center justify-between text-left">
              <Typography variant="micro" className="text-gray-dark">
                Character count: {settings.headline?.length || 0} / 40
              </Typography>
              {settings.headline?.length >= 35 && settings.headline?.length < 40 && (
                <Typography variant="micro" className="text-orange">
                  ⚠ Close to limit
                </Typography>
              )}
              {settings.headline?.length >= 40 && (
                <Typography variant="micro" className="text-red-600">
                  ⚠ Maximum length reached
                </Typography>
              )}
            </div>
          </div>
          <hr className="my-4 h-px w-full border-gray-light" />
          <div className="mb-4">
            <Typography variant="small" className="text-gray-dark">
              To change your name or location, visit the{" "}
              <a
                href="/portal/profile/combined"
                className="font-bold text-teal underline transition-colors hover:text-teal-dark">
                Details
              </a>{" "}
              section.
            </Typography>
          </div>
          <div className="flex flex-col items-center gap-4 sm:flex-row">
            <Button onClick={handleSave} loading={isSaving} disabled={isSaving} className="sm:px-12">
              Save
            </Button>
          </div>
        </div>

        {/* Preview Container */}
        <div className="max-[1023px]:static max-[1023px]:items-center min-[1024px]:items-start min-[1024px]:pt-0 sticky top-8 flex flex-col items-center pt-4">
          {/* Toggle Switch */}
          <div className="mb-4 flex items-center gap-3 rounded-lg border border-gray-light bg-white p-2 shadow-sm">
            <button
              onClick={() => setIsMobile(false)}
              className={`rounded-md px-4 py-2 text-sm font-medium transition-colors ${
                !isMobile ? "bg-teal text-white" : "bg-transparent text-gray-dark hover:bg-gray-ultralight"
              }`}>
              Desktop
            </button>
            <button
              onClick={() => setIsMobile(true)}
              className={`rounded-md px-4 py-2 text-sm font-medium transition-colors ${
                isMobile ? "bg-teal text-white" : "bg-transparent text-gray-dark hover:bg-gray-ultralight"
              }`}>
              Mobile
            </button>
          </div>
          <ListingCard data={previewData} isPreview={true} mobile={isMobile} />
        </div>
      </div>
    </div>
  )
}

const ListingCardPage = (props) => (
  <ToastProvider>
    <ListingCardPageContent {...props} />
  </ToastProvider>
)

export default ListingCardPage
